import { useI18n } from "@/composables/useI18n";
import { useToast, useUserStore } from "@/store";
import { getLocale } from "@/utils/locale";
import * as Sentry from "@sentry/browser";
import { onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useCurrentUser, useFirebaseAuth } from "vuefire";

export function useAuthenticateUser() {
  const user = useCurrentUser();
  const router = useRouter();
  const userStore = useUserStore();
  const auth = useFirebaseAuth();
  const { showErrorToast, showWarningToast } = useToast();
  const { t } = useI18n();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  if (auth) auth.languageCode = getLocale();

  // `immediate` covers the case where VueFire already hydrated before this composable runs.
  watch(
    user,
    async (currentUser, previousUser) => {
      // `undefined` means auth is still loading in VueFire
      if (currentUser === undefined) return;

      try {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }

        if (currentUser) {
          await userStore.authenticate(currentUser);
          if (!userStore.authenticatedUser) throw new Error("User is not authenticated");
          const authClaims = userStore.authenticatedUser.authClaims;

          const sessionExpirationMs = authClaims.sessionExpiration * 1000;
          const now = Date.now();
          if (now >= sessionExpirationMs) {
            console.log("Session expired, signing out");
            showWarningToast(t("errors.sessionExpired"));
            await auth?.signOut();
            return;
          }
          timeoutId = setTimeout(() => {
            console.log("Session timeout, signing out");
            showWarningToast(t("errors.sessionExpired"));
            void auth?.signOut();
          }, sessionExpirationMs - now);

          Sentry.setUser({
            email: currentUser.email || undefined,
            id: currentUser.uid,
            claims: authClaims,
          });
        } else {
          userStore.unAuthenticate();
          Sentry.setUser(null);
        }

        if (!currentUser && previousUser) {
          await router.push({ name: "Login" });
        }
      } catch (cause) {
        const error = new Error("Error during authentication state change", { cause });
        console.error(error);
        Sentry.captureException(error);
        showErrorToast();
        await auth?.signOut();
      }
    },
    { immediate: true },
  );

  onUnmounted(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  });
}
